import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom';
import { HomeIcon } from "src/components/ui-icons/menu";
import Select from 'src/pages/DashboardPage/vpo/ui/select';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { transformTitle, filterByRange } from '../../utils/utils';
import { UniversalObject, Quarter } from '../../types/data';
import { getFilters, getFetchingStatusFilters } from '../../store/filters/selectors';
import { fetchFiltersRequest } from '../../store/filters/filters';
import { fetchActionById } from '../../api/strapiGop';

type MainHeadProps = {
  group: UniversalObject | null;
  factory: UniversalObject;
  gop: UniversalObject;
  quarter: Quarter;
  year: number;
  user?: unknown;
  onGroupChange: (value: UniversalObject) => void;
  onFactoryChange: (value: UniversalObject) => void;
  onGopChange: (value: UniversalObject) => void;
  setQuarterIndex?: React.Dispatch<React.SetStateAction<number | null>>;
  setYear?: React.Dispatch<React.SetStateAction<number | null>>;
  setPendingDirectionId?: React.Dispatch<React.SetStateAction<number | null>>;
}

function MainHead(props: MainHeadProps) {
  const { group, factory, gop, quarter, year, user, onGroupChange, onFactoryChange, onGopChange, setQuarterIndex, setYear, setPendingDirectionId } = props;

  const dispatch = useAppDispatch();
  const groups = useAppSelector(getFilters);
  const loading = useAppSelector(getFetchingStatusFilters);
  const loadingUser = useAppSelector((state) => state.users.statusCurrent);
  console.log('loadingUser', loadingUser)

  const [searchParams] = useSearchParams();

  useEffect(() => {
    dispatch(fetchFiltersRequest());
  }, [dispatch]);

  //! ставлю фильтры из карточки, беру из url есть есть. (для открытия модалки, если переходить по ссылки с МЗ)
  useEffect(() => {
    // Если фильтры еще загружаются или не загружены, ничего не делаем
    if (loading || groups.length === 0) return;

    // Если пользователь еще загружается, ждем
    if (loadingUser !== 'SUCCESS') return;

    // Если фильтры уже установлены, проверяем валидность
    // Если все три фильтра заполнены И у group есть корректный id, значит они уже установлены
    if (group?.id && factory?.id && gop?.id) return;

    const actionCardId = searchParams.get('actionCardGOP');

    // Если есть actionCardId в URL, устанавливаем фильтры из карточки
    if (actionCardId) {
      fetchActionById(Number(actionCardId)).then(card => {
        //! Устанавливаем квартал и год из карточки
        if (card.quarter && card.year && setQuarterIndex && setYear) {
          const quarters: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
          const idx = quarters.findIndex((q) => q === card.quarter);
          if (idx !== -1) {
            setQuarterIndex(idx);
          }
          setYear(card.year);
        }

        if (card.directionID && setPendingDirectionId) {
          setPendingDirectionId(card.directionID);
        }

        for (const grp of groups) {
          const foundFactory = grp.factories?.find((f: any) => f.id === card.factory?.id);
          if (foundFactory) {
            const foundGop = grp.gops?.find((g: any) => g.id === card.gop?.id);

            onGroupChange(transformTitle(grp));
            onFactoryChange(transformTitle(foundFactory));
            if (foundGop) onGopChange(transformTitle(foundGop));
            return;
          }
        }
      }).catch(err => console.error('Ошибка загрузки карточки:', err));
      return;
    }

    // Дефолтные фильтры если нет actionCardGOP
    const defaultGroup = transformTitle(groups[0]);
    const defaultFactory = transformTitle(defaultGroup.factories?.[0]) || null;
    const filteredGops = filterByRange(defaultGroup.gops, quarter, year);
    const sortGops = filteredGops.sort((a, b) =>
        a.order === null ? 1 :
        b.order === null ? -1 :
        a.order - b.order
      );
    const defaultGop = transformTitle(sortGops?.[0]) || null;

    // Устанавливаем по дефолту группу и завод пользователя
    let currentGroup = null;
    let currentFactory = null;
    let currentGop = null;

    if (user.infoVPO.directoryTitle.length > 0) {
      currentGroup = groups.find((group) => group.title === user.infoVPO.directoryTitle[0]);
      const filteredGopsCurrent = filterByRange(currentGroup.gops, quarter, year);
      const sortGopsCurrent = filteredGopsCurrent.sort((a, b) =>
          a.order === null ? 1 :
          b.order === null ? -1 :
          a.order - b.order
        );
      currentGop = transformTitle(sortGopsCurrent?.[0]) || null;
    }

    if (user.infoVPO.factory.length > 0 && currentGroup) {
      currentFactory = currentGroup.factories.find((factory) => factory.title === user.infoVPO.factory[0].factoryTitle);
    }

    currentGroup ? onGroupChange(transformTitle(currentGroup)) : onGroupChange(defaultGroup);
    currentFactory ? onFactoryChange(transformTitle(currentFactory)) : onFactoryChange(defaultFactory);
    currentGop ? onGopChange(currentGop) : onGopChange(defaultGop);
  }, [groups, quarter, year, loading, loadingUser, searchParams]);

  const uniqueGroups = groups.map(transformTitle);

  const factories = (group?.factories ?? []).map(transformTitle);
  const gops = (group?.gops ?? []).map(transformTitle);

  const arrayGops = useMemo(
      () => filterByRange(gops, quarter, year)
        .sort((a, b) =>
          a.order === null ? 1 :
          b.order === null ? -1 :
          a.order - b.order
        ),
      [gops, year, quarter]);

  const handleSelectChangeGroup = (newValue: { [key: string]: string | number }) => {
    const currentId = newValue.id;
    const currentGroup = groups.find((group) => group.id === currentId);
    onGroupChange(transformTitle(currentGroup));
    onFactoryChange(transformTitle(currentGroup.factories?.[0]) || null);

    const sortedGops = [...currentGroup.gops].sort((a, b) =>
          a.order === null ? 1 :
          b.order === null ? -1 :
          a.order - b.order
        );
    onGopChange(transformTitle(sortedGops[0]) || null);
  };

  const handleSelectChangeFilter = (newValue: { [key: string]: string | number }) => {
    onFactoryChange(newValue);
  };

  const handleSelectChangeIndicator = (newValue: { [key: string]: string | number }) => {
    onGopChange(newValue);
  };

  return (
    <div className="px-7.5 py-4 border-b flex items-center gap-2">
      <HomeIcon />
      <p className="mr-auto">Главная</p>
      {!loading && loadingUser === 'SUCCESS' &&
        <>
          <Select selected={group} data={uniqueGroups} onChange={handleSelectChangeGroup} />
          <Select selected={factory} data={factories} onChange={handleSelectChangeFilter} />
          <Select selected={gop} data={arrayGops} width='500px' onChange={handleSelectChangeIndicator} />
        </>}
    </div>
  );
}

export default MainHead;
